/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import type { CountryCallingCode } from "libphonenumber-js";
import { useState, type ReactNode } from "react";

import { cssFns, type Style } from "@superweb/css";
import { useFlag } from "@superweb/flags";
import { useLocale } from "@superweb/intl";
import {
  ExperimentalForm,
  ExperimentalFormRow,
  PhoneField,
  TextField,
  createComboBoxState,
  createPhoneFieldState,
  createTextFieldState,
  createRadioGroupState,
  createDateFieldState,
  useDebouncedState,
  useIsMobile,
  useTypo,
  useUiColors,
  type ComboBoxOption,
  type ComboBoxState,
  type PhoneFieldState,
  type TextFieldState,
} from "@superweb/ui";

import {
  useV1PersonalSubmitPost,
  useV1StationsGet,
} from "@fleet/api/fleet-forms";
import { useAction } from "@fleet/api";

import { useLogAction } from "#internal/analytics";
import { Message, useMessage } from "#internal/intl";
import {
  Banner,
  FormSuggest,
  PhoneVerificator,
  SubmitBottomSheet,
  useOperationIdContext,
  AddressSuggest,
} from "#internal/ui";
import {
  useErrorMessages,
  useOnChangePartial,
  useOnShowErrors,
  useValidate,
} from "#internal/utils";
import { useProfile } from "#internal/auth";

import type { ParkInfoData } from "./form";
import { useIsIos } from "./use-is-ios";
import {
  VehicleDetailsFields,
  type VehicleFormState,
} from "./vehicle-details-fields";
import {
  DriverLicenseDetailsFields,
  type DriverLicenseDetailsState,
} from "./driver-license-details-fields";
import {
  RegionDetailsFields,
  type RegionDetailsState,
} from "./region-details-fields";

type PersonalFormState = {
  phone: PhoneFieldState;
  first_name: TextFieldState;
  last_name: TextFieldState;
  address: ComboBoxState<ComboBoxOption>;
  address_apartment: TextFieldState;
  doc_address: ComboBoxState<ComboBoxOption>;
  tax_identification_number: TextFieldState;
  tax_system: ComboBoxState<ComboBoxOption>;
  email: TextFieldState;
  tax_authority_code: TextFieldState;
  station_id: ComboBoxState<ComboBoxOption>;
};

type Error =
  | "invalid_personal_data"
  | "invalid_phone_number"
  | "invalid_car_number"
  | "invalid_driver_license"
  | "invalid_issue_date"
  | "invalid_expiry_date"
  | "agree_required"
  | "submit_error"
  | "need_confirmation_data"
  | "verification_phone_failed"
  | "inn_required"
  | "inn_verification_failed"
  | "phone_verification_fns_failed"
  | "contractor_already_exists"
  | "idempotency_token_required"
  | "no_park_for_lead"
  | "lead_already_exists";

export const RegisterForm = ({
  countryCode,
  onSubmit,
  refId,
  onError,
  onSuccess,
  parkInfo,
  bottomSheetContent,
}: {
  countryCode?: CountryCallingCode;
  refId: string;
  onError: (code?: Error) => void;
  onSuccess: () => void;
  onSubmit: () => void;
  parkInfo: ParkInfoData;
  bottomSheetContent?: ReactNode;
}) => {
  const message = useMessage();
  const logActionSubmit = useLogAction("submit");
  const logActionInvalid = useLogAction("invalid");
  const locale = useLocale();
  const errorMessages = useErrorMessages();
  const typo = useTypo();
  const uiColors = useUiColors();
  const flag = useFlag();
  const { info: profileInfo, authentificationEnabled } = useProfile();
  const isIos = useIsIos();
  const isMobile = useIsMobile();

  const enableNewApi = flag({
    id: "280f006d-a58a-40ad-bb83-ddb6fc333258",
    description: "Enable new api",
  });

  const hasVehicleSection =
    enableNewApi && parkInfo.form_requirements.vehicle === "required";
  const hasLicenseSection =
    enableNewApi && parkInfo.form_requirements.license === "required";
  const hasRegionSection =
    enableNewApi && parkInfo.form_requirements.region === "required";

  const { submissionId, updateSubmissionId } = useOperationIdContext();

  const enablePhoneVerification = Boolean(
    parkInfo.phone_verification && !authentificationEnabled,
  );

  const enableTestPhones = flag({
    id: "aa814bf7-a087-4378-934f-9374140551cf",
    description: "Enable test phones for testing",
  });

  const additionalFieldsMap = {
    ...parkInfo.optional_fields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: false },
      }),
      {},
    ),
    ...parkInfo.required_fields?.reduce<Record<string, { required: boolean }>>(
      (res, item) => ({
        ...res,
        [item]: { required: true },
      }),
      {},
    ),
  };

  const taxSystemOptions = [
    {
      key: "USN",
      label: message({
        id: "5e4f8e56-aae2-425d-88c6-9be0aabbeef4",
        context:
          "Drivers. Drivers form. Details group. Tax system option (USN)",
        default: "Simplified tax system (USN)",
      }),
    },
    {
      key: "USNP",
      label: message({
        id: "16bdb9c2-a0c4-44a3-b1a3-2fd79f317fde",
        context:
          "Drivers. Drivers form. Details group. Tax system option (USNP)",
        default: "Patent tax system (USN)",
      }),
    },
    {
      key: "OSNP",
      label: message({
        id: "d822b300-8d63-4a83-8d86-b01b50c15261",
        context:
          "Drivers. Drivers form. Details group. Tax system option (OSNP)",
        default: "Patent tax system (OSN)",
      }),
    },
    {
      key: "AUSN",
      label: message({
        id: "68f2847d-5bba-4178-9245-46ab48e4002e",
        context:
          "Drivers. Drivers form. Details group. Tax system option (AUTOUSN)",
        default: "Automated simplified tax system (AUSN)",
      }),
    },
    {
      key: "OSN",
      label: message({
        id: "4efe1cba-c103-4005-8693-b8e97932dbf3",
        context:
          "Drivers. Drivers form. Details group. Tax system option (OSN)",
        default: "General tax system (OSN)",
      }),
    },
  ];

  const [personalState, setPersonalState] = useState<PersonalFormState>(() => ({
    phone: createPhoneFieldState({
      value: profileInfo?.phone || (countryCode ? "+" + countryCode : ""),
      errorMessage: profileInfo?.phone
        ? undefined
        : countryCode
          ? errorMessages.invalidPhone
          : errorMessages.requiredField,
    }),
    first_name: createTextFieldState({
      value: profileInfo?.firstName ?? "",
      errorMessage: profileInfo?.firstName
        ? undefined
        : errorMessages.requiredField,
    }),
    last_name: createTextFieldState({
      value: profileInfo?.lastName ?? "",
      errorMessage: profileInfo?.lastName
        ? undefined
        : errorMessages.requiredField,
    }),
    address: createComboBoxState({
      errorMessage: additionalFieldsMap["address"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    address_apartment: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["address_apartment"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    doc_address: createComboBoxState({
      errorMessage: additionalFieldsMap["doc_address"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_identification_number: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["tax_identification_number"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_system: createComboBoxState({
      errorMessage: additionalFieldsMap["tax_system"]
        ? errorMessages.requiredField
        : undefined,
    }),
    email: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["email"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    tax_authority_code: createTextFieldState({
      value: "",
      errorMessage: additionalFieldsMap["tax_authority_code"]?.required
        ? errorMessages.requiredField
        : undefined,
    }),
    station_id: createComboBoxState({
      errorMessage: additionalFieldsMap["station_id"]
        ? errorMessages.requiredField
        : undefined,
    }),
  }));

  const enableVehicleTypesSelection = flag({
    id: "a8f32eab-af01-4b90-a0d9-cfe46f3d08a3",
    description: "Enable vehicle types selection",
  });

  const [vehicleState, setVehicleState] = useState(() => ({
    number: createTextFieldState({
      value: "",
      errorMessage: errorMessages.requiredField,
    }),
    vehicle_type: createRadioGroupState<"car" | "bike" | "rickshaw">({
      value: !enableVehicleTypesSelection
        ? "car"
        : parkInfo.vehicle_types.length === 1
          ? parkInfo.vehicle_types[0]?.id
          : undefined,
      errorMessage:
        enableVehicleTypesSelection && parkInfo.vehicle_types.length > 1
          ? errorMessages.requiredField
          : undefined,
    }),
    brand: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    model: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    color_code: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
    year: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const onChangeVehiclePartial = useOnChangePartial(setVehicleState);
  const onShowVehicleErrors = useOnShowErrors(setVehicleState);

  const [licenseState, setLicenseState] = useState<DriverLicenseDetailsState>(
    () => ({
      number: createTextFieldState({
        value: "",
        errorMessage: errorMessages.requiredField,
      }),
      issue_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
      expiry_date: createDateFieldState({
        errorMessage: errorMessages.requiredField,
      }),
    }),
  );

  const onChangeLicensePartial = useOnChangePartial(setLicenseState);
  const onShowLicenseErrors = useOnShowErrors(setLicenseState);

  const [regionState, setRegionState] = useState<RegionDetailsState>(() => ({
    region: createComboBoxState({
      errorMessage: errorMessages.requiredField,
    }),
  }));

  const onShowRegionErrors = useOnShowErrors(setRegionState);

  const [confirmationId, setConfirmationId] = useState("");

  const debouncedStationState = useDebouncedState(
    personalState.station_id,
    300,
  );

  const { data: stationData } = useV1StationsGet(
    {
      query: {
        ref_id: refId,
        ...(debouncedStationState.inputValue && {
          text_query: debouncedStationState.inputValue,
        }),
      },
      headers: {
        language: locale.toString(),
      },
    },
    {
      enabled: !!additionalFieldsMap["station_id"],
    },
  );

  const stationOptions =
    stationData?.data.stations.map((item) => ({
      key: item.id,
      label: item.taxi_station_name,
    })) ?? [];

  const onChangePersonalPartial = useOnChangePartial(setPersonalState);
  const onShowPersonalErrors = useOnShowErrors(setPersonalState);

  const { mutate: submit, isLoading } = useV1PersonalSubmitPost({
    onSuccess(data) {
      if (data.ok) {
        onSuccess();
        onSubmit();
        logActionSubmit("submit_form", {
          params: authentificationEnabled ? "auth" : "no_auth",
        });
      } else {
        updateSubmissionId();

        switch (data.error.code) {
          case "invalid_phone_number": {
            logActionInvalid("invalid_phone_number", {
              onlyYM: true,
            });
            onChangePersonalPartial({
              phone: {
                ...personalState.phone,
                errorMessage: errorMessages.invalidPhone,
                errorVisible: true,
              },
            });
            break;
          }
          default: {
            onError(data.error.code);
            break;
          }
        }
      }
    },
    onError: () => {
      updateSubmissionId();
      onError();
    },
  });

  const { action: actionSubmit } = useAction({
    url: "/fleet-forms/selfreg/v1/submit",
    method: "POST",
  });

  const { validateTextField, validateComboboxField, validatePhoneField } =
    useValidate();

  const linkStyles: Style = {
    color: uiColors.text,
    ...cssFns.border({ radius: "6px" }),
    ...typo({
      level: "caption1",
      weight: "regular",
      density: "tight",
    }),
    outlineStyle: "none",
    cursor: "pointer",
  };

  const optionalFieldDescription = message({
    id: "a7fa8cad-6a0e-468e-8dab-1330d779eb7c",
    context: "Yango forms. Optional field description",
    default: "Optional",
  });

  const enableSingleDownloadButton = flag({
    id: "ea22a0b5-1ea4-45b0-bdd1-b490a82fbb41",
    description: "Enable single button for downloading My Tax app",
  });
  const showSingleDownloadButton = isMobile && enableSingleDownloadButton;
  const selfemployedBannerLink = isIos
    ? parkInfo.link_to_selfemployed_app?.ios_app
    : parkInfo.link_to_selfemployed_app?.android_app;

  const selfemployedLinks = [
    ...(parkInfo.link_to_selfemployed_app?.android_app
      ? [
          {
            text: message({
              id: "d5c0f99b-b29b-4486-a4ea-7cf529b376b8",
              context:
                "Register form. Self employed banner. Download button. Android",
              default: "Android",
            }),
            href: parkInfo.link_to_selfemployed_app.android_app,
          },
        ]
      : []),
    ...(parkInfo.link_to_selfemployed_app?.ios_app
      ? [
          {
            text: message({
              id: "806b6984-51c9-45cd-892d-e4430627c046",
              context:
                "Register form. Self employed banner. Download button. IOS",
              default: "iOS",
            }),
            href: parkInfo.link_to_selfemployed_app.ios_app,
          },
        ]
      : []),
  ];
  const entrepreneurLinks = parkInfo.link_to_individual_entrepreneur_guide
    ? [
        {
          text: message({
            id: "9e146d4b-c0fe-4bfc-b613-5b23991f1145",
            context: "Register form. Individual entrepreneur banner. Button",
            default: "How to register as an individual entrepreneur",
          }),
          href: parkInfo.link_to_individual_entrepreneur_guide,
        },
      ]
    : [];

  const isAddressFieldsDisabled = enablePhoneVerification && !confirmationId;

  return (
    <ExperimentalForm
      main={
        <>
          {parkInfo.employment_banner_text ? (
            <Banner
              title={parkInfo.employment_banner_text}
              linkButtons={
                parkInfo.employment_type === "selfemployed"
                  ? showSingleDownloadButton && selfemployedBannerLink
                    ? [
                        {
                          text: message({
                            id: "2d6f5a49-70ec-47fa-84bf-960757aa313c",
                            context:
                              "Register form. Self employed banner. Download button",
                            default: "Download",
                          }),
                          href: selfemployedBannerLink,
                        },
                      ]
                    : selfemployedLinks
                  : entrepreneurLinks
              }
            />
          ) : null}
          <ExperimentalFormRow columnsCount={enablePhoneVerification ? 2 : 1}>
            <PhoneField
              required
              disabled={authentificationEnabled && Boolean(profileInfo?.phone)}
              label={message({
                id: "41652579-a5a6-45f4-a3ce-2e9ca03078a3",
                context: "Yango forms. Phone number field",
                default: "Phone number",
              })}
              state={personalState.phone}
              onChange={(state) => {
                setConfirmationId("");
                const errorMessage = validatePhoneField(state.value, {
                  isPhoneInvalid: enableTestPhones
                    ? state.invalid && !state.value.match(/^\+7000[0-9]{7}$/)
                    : state.invalid,
                });
                onChangePersonalPartial({
                  phone: {
                    ...state,
                    errorMessage,
                  },
                });
              }}
            />
            {enablePhoneVerification && (
              <PhoneVerificator
                phone={
                  !personalState.phone.invalid &&
                  !personalState.phone.errorMessage
                    ? personalState.phone.value
                    : ""
                }
                state={confirmationId}
                onChange={setConfirmationId}
              />
            )}
          </ExperimentalFormRow>
          <ExperimentalFormRow columnsCount={2}>
            <TextField
              required
              state={personalState.first_name}
              label={message({
                id: "e0389ece-798a-4f9f-8182-7a8597fc43c5",
                context: "Yango form. Name field",
                default: "First name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  first_name: {
                    ...state,
                    errorMessage,
                    errorVisible: !!errorMessage,
                  },
                });
              }}
            />
            <TextField
              required
              state={personalState.last_name}
              label={message({
                id: "2300180e-4699-4262-9b17-1f17972df3c3",
                context: "Yango forms. Surname field",
                default: "Last name",
              })}
              onChange={(state) => {
                const errorMessage = validateTextField(state.value);
                onChangePersonalPartial({
                  last_name: {
                    ...state,
                    errorMessage,
                    errorVisible: !!errorMessage,
                  },
                });
              }}
            />
          </ExperimentalFormRow>
          {Object.keys(additionalFieldsMap).length > 0 &&
            !additionalFieldsMap["station_id"] && (
              <span
                css={{
                  ...typo({
                    level: "body2",
                    weight: "medium",
                    density: "tight",
                  }),
                  ...cssFns.padding("0", "16px"),
                }}
              >
                {message({
                  id: "7b6abcd3-badc-4fab-a813-5f9069d053a6",
                  context: "Yango forms. Additional fields title",
                  default: "Required to get receipts ",
                })}
              </span>
            )}
          {additionalFieldsMap["address"] && (
            <ExperimentalFormRow columnsCount={2}>
              <AddressSuggest
                refId={refId}
                phone={personalState.phone.value}
                disabled={isAddressFieldsDisabled}
                required={additionalFieldsMap["address"].required}
                label={message({
                  id: "94047075-d953-4a2b-a903-0c9e79e17f42",
                  context: "Yango forms. Actual adress field",
                  default: "Current address",
                })}
                description={
                  additionalFieldsMap["address"].required
                    ? undefined
                    : optionalFieldDescription
                }
                confirmationId={confirmationId}
                enablePhoneVerification={enablePhoneVerification}
                state={personalState.address}
                onChange={(state) => {
                  const errorMessage = additionalFieldsMap["address"]?.required
                    ? validateComboboxField(state)
                    : undefined;

                  onChangePersonalPartial({
                    address: {
                      ...state,
                      errorMessage,
                      errorVisible: Boolean(errorMessage),
                    },
                  });
                }}
              />
              <TextField
                required={additionalFieldsMap["address_apartment"]?.required}
                state={personalState.address_apartment}
                disabled={isAddressFieldsDisabled}
                label={message({
                  id: "959b4af7-bdf6-46a7-9608-bd8985e299e4",
                  context: "Yango forms. Actual adress apartment field",
                  default: "Apartment",
                })}
                description={
                  additionalFieldsMap["address_apartment"]?.required
                    ? undefined
                    : optionalFieldDescription
                }
                onChange={(state) => {
                  onChangePersonalPartial({
                    address_apartment: {
                      ...state,
                    },
                  });
                }}
              />
            </ExperimentalFormRow>
          )}
          {additionalFieldsMap["doc_address"] && (
            <ExperimentalFormRow columnsCount={2}>
              <AddressSuggest
                refId={refId}
                phone={personalState.phone.value}
                confirmationId={confirmationId}
                enablePhoneVerification={enablePhoneVerification}
                disabled={isAddressFieldsDisabled}
                required={additionalFieldsMap["doc_address"].required}
                label={message({
                  id: "8eaea192-11ce-46a6-997c-5bc23ee8b4c4",
                  context: "Yango forms. Registration adress field",
                  default: "Registration address",
                })}
                description={
                  additionalFieldsMap["doc_address"].required
                    ? undefined
                    : optionalFieldDescription
                }
                state={personalState.doc_address}
                onChange={(state) => {
                  const errorMessage = additionalFieldsMap["doc_address"]
                    ?.required
                    ? validateComboboxField(state)
                    : undefined;

                  onChangePersonalPartial({
                    doc_address: {
                      ...state,
                      errorMessage,
                      errorVisible: Boolean(errorMessage),
                    },
                  });
                }}
              />
            </ExperimentalFormRow>
          )}
          {(additionalFieldsMap["tax_identification_number"] ||
            additionalFieldsMap["tax_system"]) && (
            <ExperimentalFormRow columnsCount={2}>
              {additionalFieldsMap["tax_identification_number"] && (
                <TextField
                  required={
                    additionalFieldsMap["tax_identification_number"].required
                  }
                  state={personalState.tax_identification_number}
                  label={
                    parkInfo.country_code === "UZ"
                      ? message({
                          id: "6fd7f096-c3b9-4aaa-88ec-b6c48992d6b9",
                          context: "Register form. Identification number",
                          default: "Taxpayer ID",
                        })
                      : parkInfo.country_code === "MD"
                        ? message({
                            id: "da64d766-c598-4c11-a22d-fdf3303d3df6",
                            context:
                              "Register form. Identification number in Moldova",
                            default: "IDNP",
                          })
                        : message({
                            id: "5a130c9e-e1be-48bc-a8a5-41e0eccdabaf",
                            context: "Yango forms. Taxpayer ID field",
                            default: "INN",
                          })
                  }
                  description={
                    additionalFieldsMap["tax_identification_number"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap[
                      "tax_identification_number"
                    ]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      tax_identification_number: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["tax_system"] && (
                <FormSuggest
                  required={additionalFieldsMap["tax_system"].required}
                  data={taxSystemOptions}
                  label={message({
                    id: "827c5a07-baa0-4391-bccf-02afbed77162",
                    context: "Yango forms. Tax system field",
                    default: "Tax system",
                  })}
                  description={
                    additionalFieldsMap["tax_system"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  state={personalState.tax_system}
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["tax_system"]
                      ?.required
                      ? validateComboboxField(state)
                      : undefined;
                    onChangePersonalPartial({
                      tax_system: {
                        ...state,
                        errorMessage,
                        errorVisible: Boolean(errorMessage),
                      },
                    });
                  }}
                />
              )}
            </ExperimentalFormRow>
          )}
          {(additionalFieldsMap["email"] ||
            additionalFieldsMap["tax_authority_code"] ||
            additionalFieldsMap["station_id"]) && (
            <ExperimentalFormRow columnsCount={2}>
              {additionalFieldsMap["email"] && (
                <TextField
                  required={additionalFieldsMap["email"].required}
                  state={personalState.email}
                  label={message({
                    id: "fdd2e68c-cfc5-4de0-bcdf-c71369d22eb3",
                    context: "Yango forms. Email field",
                    default: "Email",
                  })}
                  description={
                    additionalFieldsMap["email"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["email"]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      email: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["tax_authority_code"] && (
                <TextField
                  required={additionalFieldsMap["tax_authority_code"].required}
                  state={personalState.tax_authority_code}
                  label={message({
                    id: "617734a2-666e-462a-a9ea-f598a06a6300",
                    context: "Yango forms. Tax authority code field",
                    default: "Tax authority code",
                  })}
                  description={
                    additionalFieldsMap["tax_authority_code"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap[
                      "tax_authority_code"
                    ]?.required
                      ? validateTextField(state.value)
                      : undefined;
                    onChangePersonalPartial({
                      tax_authority_code: {
                        ...state,
                        errorMessage,
                        errorVisible: !!errorMessage,
                      },
                    });
                  }}
                />
              )}
              {additionalFieldsMap["station_id"] && (
                <FormSuggest
                  required={additionalFieldsMap["station_id"].required}
                  data={stationOptions}
                  label={message({
                    id: "9041397d-baa5-4884-84ca-9880ff4bd19a",
                    context: "Yango forms. Station field",
                    default: "Taxi station",
                  })}
                  description={
                    additionalFieldsMap["station_id"].required
                      ? undefined
                      : optionalFieldDescription
                  }
                  state={personalState.station_id}
                  onChange={(state) => {
                    const errorMessage = additionalFieldsMap["station_id"]
                      ?.required
                      ? validateComboboxField(state)
                      : undefined;
                    onChangePersonalPartial({
                      station_id: {
                        ...state,
                        errorMessage,
                        errorVisible: Boolean(errorMessage),
                      },
                    });
                  }}
                />
              )}
            </ExperimentalFormRow>
          )}
          {hasRegionSection && (
            <RegionDetailsFields
              state={regionState}
              onChange={setRegionState}
              refId={refId}
            />
          )}
          {hasVehicleSection && (
            <>
              <div
                css={{
                  ...typo({
                    level: "body2",
                    density: "normal",
                    weight: "medium",
                  }),
                }}
              >
                <Message
                  id="cc161436-76bd-48c4-845f-125011b46deb"
                  context="Forms. Vehicle details section. Title"
                  default="Vehicle"
                />
              </div>
              <VehicleDetailsFields
                refId={refId}
                state={vehicleState}
                onChangePartial={onChangeVehiclePartial}
                enableVehicleTypesSelection={enableVehicleTypesSelection}
                vehicleTypes={parkInfo.vehicle_types}
              />
            </>
          )}
          {hasLicenseSection && (
            <>
              <div
                css={{
                  ...typo({
                    level: "body2",
                    density: "normal",
                    weight: "medium",
                  }),
                }}
              >
                <Message
                  id="5927595c-a5da-4d9f-a204-1d13be5b18b0"
                  context="Forms. License details section. Title"
                  default="License"
                />
              </div>
              <DriverLicenseDetailsFields
                state={licenseState}
                onChangePartial={onChangeLicensePartial}
                countryCode={parkInfo.country_code}
              />
            </>
          )}
          <SubmitBottomSheet
            text={message({
              id: "ca0dfe03-7215-463e-8f07-d1db5c04a302",
              context: "Yango forms. Submit button text",
              default: "Register",
            })}
            isLoading={isLoading}
          >
            {bottomSheetContent ?? (
              <Message
                id="3de1d8df-aebb-4759-98bb-362e5044bc37"
                context="Yango forms. Agree processing data. Title"
                default="By submitting this form, I accept the <terms_of_use>Terms Of Use</terms_of_use>, agree to the processing of my personal data for the purpose of reviewing my application, and confirm that I have read the <privacy_notice>Privacy Notice</privacy_notice>."
                __experimental_tags={{
                  terms_of_use: (chunks: ReactNode) => (
                    <a
                      target="_blank"
                      href={parkInfo.legal_docs_urls.url_terms_of_use}
                      css={linkStyles}
                      key="terms_of_use"
                    >
                      {chunks}
                    </a>
                  ),
                  privacy_notice: (chunks: ReactNode) => (
                    <a
                      target="_blank"
                      href={parkInfo.legal_docs_urls.url_privacy_policy}
                      css={linkStyles}
                      key="privacy_notice"
                    >
                      {chunks}
                    </a>
                  ),
                }}
              />
            )}
          </SubmitBottomSheet>
        </>
      }
      onSubmit={() => {
        if (hasVehicleSection) {
          onShowVehicleErrors();
        }

        const hasVehicleErrors =
          hasVehicleSection &&
          Object.keys(vehicleState).some(
            (field) =>
              vehicleState[field as keyof VehicleFormState].errorMessage !==
              undefined,
          );

        if (hasVehicleErrors) {
          if (
            vehicleState.number.errorMessage === errorMessages.invalidCarNumber
          ) {
            logActionInvalid("invalid_car_number", { onlyYM: true });
          }
        }

        if (hasRegionSection) {
          onShowRegionErrors();
        }

        const hasRegionErrors =
          hasRegionSection &&
          Object.keys(regionState).some(
            (field) =>
              regionState[field as keyof RegionDetailsState].errorMessage !==
              undefined,
          );

        if (hasLicenseSection) {
          onShowLicenseErrors();
        }

        const hasLicenseErrors =
          hasLicenseSection &&
          Object.keys(licenseState).some(
            (field) =>
              licenseState[field as keyof DriverLicenseDetailsState]
                .errorMessage !== undefined,
          );

        if (hasLicenseErrors) {
          if (
            (parkInfo.license_number_templates &&
              parkInfo.license_number_templates.length > 0 &&
              licenseState.number.errorMessage ===
                errorMessages.invalidDriverLicenseWithTemplates(
                  parkInfo.license_number_templates.join(", "),
                )) ||
            licenseState.number.errorMessage ===
              errorMessages.invalidDriverLicense
          ) {
            logActionInvalid("invalid_driver_license", {
              onlyYM: true,
            });
          }

          if (
            licenseState.expiry_date.errorMessage === errorMessages.invalidDate
          ) {
            logActionInvalid("invalid_expiry_date", {
              onlyYM: true,
            });
          }

          if (
            licenseState.issue_date.errorMessage === errorMessages.invalidDate
          ) {
            logActionInvalid("invalid_issue_date", {
              onlyYM: true,
            });
          }
        }

        onShowPersonalErrors();

        const hasErrors = Object.keys(personalState).some(
          (field) =>
            personalState[field as keyof PersonalFormState].errorMessage !==
            undefined,
        );

        if (hasErrors) {
          if (personalState.phone.errorMessage === errorMessages.invalidPhone) {
            logActionInvalid("invalid_phone_number", {
              onlyYM: true,
            });
          }
        }

        if (
          hasErrors ||
          hasLicenseErrors ||
          hasVehicleErrors ||
          hasRegionErrors
        ) {
          return;
        }

        const personal = {
          first_name: personalState.first_name.value,
          last_name: personalState.last_name.value,
          phone: personalState.phone.value,
          ref_id: refId,
          agree_processing_data: true,
          agree_receive_info: true,
          address: personalState.address.value?.key,
          address_apartment: sendValueOrUndefined(
            personalState.address_apartment.value,
          ),
          doc_address: personalState.doc_address.value?.key,
          tax_identification_number: sendValueOrUndefined(
            personalState.tax_identification_number.value,
          ),
          tax_system: personalState.tax_system.value?.key,
          tax_authority_code: sendValueOrUndefined(
            personalState.tax_authority_code.value,
          ),
          email: sendValueOrUndefined(personalState.email.value),
          confirmation_id: sendValueOrUndefined(confirmationId),
          station_id: personalState.station_id.value?.key,
        } as const;

        const car =
          vehicleState.brand.value?.key &&
          vehicleState.model.value?.key &&
          vehicleState.color_code.value?.key &&
          vehicleState.year.value?.key
            ? {
                number: vehicleState.number.value,
                brand: vehicleState.brand.value.key,
                model: vehicleState.model.value.key,
                color_code: vehicleState.color_code.value.key,
                year: Number(vehicleState.year.value.key),
              }
            : undefined;

        const license =
          licenseState.issue_date.value && licenseState.expiry_date.value
            ? {
                number: licenseState.number.value,
                issue_date: licenseState.issue_date.value.toString(),
                expiry_date: licenseState.expiry_date.value.toString(),
              }
            : undefined;

        const park = regionState.region.value
          ? {
              region_id: regionState.region.value.key,
            }
          : undefined;

        if (enableNewApi) {
          actionSubmit(
            {
              headers: {
                "x-submission-id": submissionId,
                language: locale.toString(),
                "content-type": "application/json",
              },
              query: {
                ref_id: refId,
              },
              body: {
                personal,
                ...(hasVehicleSection && {
                  car,
                }),
                ...(hasLicenseSection && {
                  license,
                }),
                ...(hasRegionSection && {
                  park,
                }),
              },
            },
            {
              onResponse: (res) => {
                if (res.status === 204) {
                  onSuccess();
                  onSubmit();
                  logActionSubmit("submit_form", {
                    params: authentificationEnabled ? "auth" : "no_auth",
                  });
                  return;
                }

                updateSubmissionId();

                switch (res.body.code) {
                  case "invalid_phone_number": {
                    logActionInvalid("invalid_phone_number", {
                      onlyYM: true,
                    });
                    onChangePersonalPartial({
                      phone: {
                        ...personalState.phone,
                        errorMessage: errorMessages.invalidPhone,
                        errorVisible: true,
                      },
                    });
                    break;
                  }
                  case "invalid_car_number": {
                    logActionInvalid("invalid_car_number", { onlyYM: true });
                    onChangeVehiclePartial({
                      number: {
                        ...vehicleState.number,
                        errorMessage: errorMessages.invalidCarNumber,
                        errorVisible: true,
                      },
                    });
                    break;
                  }
                  case "invalid_driver_license": {
                    logActionInvalid("invalid_driver_license", {
                      onlyYM: true,
                    });
                    onChangeLicensePartial({
                      number: {
                        ...licenseState.number,
                        errorMessage:
                          parkInfo.license_number_templates &&
                          parkInfo.license_number_templates.length > 0
                            ? errorMessages.invalidDriverLicenseWithTemplates(
                                parkInfo.license_number_templates.join(", "),
                              )
                            : errorMessages.invalidDriverLicense,
                        errorVisible: true,
                      },
                    });
                    break;
                  }
                  case "invalid_issue_date": {
                    logActionInvalid("invalid_issue_date", { onlyYM: true });
                    onChangeLicensePartial({
                      issue_date: {
                        ...licenseState.issue_date,
                        errorMessage: errorMessages.invalidDate,
                        errorVisible: true,
                      },
                    });
                    break;
                  }
                  case "invalid_expiry_date": {
                    logActionInvalid("invalid_expiry_date", {
                      onlyYM: true,
                    });
                    onChangeLicensePartial({
                      expiry_date: {
                        ...licenseState.expiry_date,
                        errorMessage: errorMessages.invalidDate,
                        errorVisible: true,
                      },
                    });
                    break;
                  }
                  default: {
                    onError(res.body.code);
                    break;
                  }
                }
                return;
              },
            },
          );
        } else {
          submit({
            headers: {
              "x-submission-id": submissionId,
              language: locale.toString(),
              "content-type": "application/json",
            },
            body: personal,
          });
        }
      }}
    />
  );
};

const sendValueOrUndefined = (value: string) => {
  return value.length > 0 ? value : undefined;
};
